import { faEdit } from '@fortawesome/pro-regular-svg-icons'
import { Box, Flex, Loader, Switch, Text } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useAppDispatch } from '@flecto-io/core'
import { getBookingById } from '@flecto-io/core/src/actions'
import { useAnalytics } from '@flecto/analytics'
import { useQueryGetBookingById } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBooking } from '@flecto/types'
import { BadgeEditable, Icon } from '@flecto/ui'
import { valueFromCents } from '@flecto/ui/lib/utils'

import { useBookingTaxesToggle } from '../../../../../bookings'

type TaxesValueProps = {
  booking?: CoreBooking
}

export function TaxesValue({ booking }: TaxesValueProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })
  const track = useAnalytics('segment')
  const dispatch = useAppDispatch()
  const bookingId = booking?.internal_id ?? ''
  const { refetch: reloadBooking } = useQueryGetBookingById({ bookingId })
  const { toggleTaxes, isChecked, isLoading } = useBookingTaxesToggle({
    bookingId,
  })
  const [isTaxablePopover, setIsTaxablePopover] = useState(isChecked)
  const taxesAmount = valueFromCents(booking?.meta?.rental_vat_cents ?? 0)

  const disabledEdit =
    booking?.status === 'completed' || booking?.status === 'canceled'

  useEffect(() => {
    setIsTaxablePopover(isChecked)
  }, [isChecked])

  const handleApply = async () => {
    await toggleTaxes(isTaxablePopover)

    if (bookingId) {
      track('BOOKING__TOGGLE_TAXES', {
        booking_id: bookingId,
        taxes: isTaxablePopover ? 'on' : 'off',
      })
      reloadBooking()
      dispatch(
        getBookingById({
          id: String(bookingId),
        })
      )
    }
  }

  return (
    <Box mr={-8}>
      <BadgeEditable
        value={taxesAmount.currency}
        color='gray'
        rightSection={!disabledEdit && <Icon icon={faEdit} size='xs' ml='xs' />}
        popoverProps={{
          position: 'bottom-end',
          withArrow: false,
          disabled: disabledEdit,
        }}
      >
        <BadgeEditable.Dropdown>
          <Flex gap='sm' justify='space-between' mb='sm'>
            <Text fw={500}>{t('overview.apply_taxes')}</Text>
            <Switch
              checked={isTaxablePopover}
              disabled={isLoading}
              thumbIcon={isLoading ? <Loader size='0.8rem' /> : null}
              onChange={(e) => {
                setIsTaxablePopover(e.currentTarget.checked)
              }}
            />
          </Flex>
        </BadgeEditable.Dropdown>
        <BadgeEditable.Close onClick={() => setIsTaxablePopover(isChecked)} />
        <BadgeEditable.Confirm onClick={handleApply} loading={isLoading} />
      </BadgeEditable>
    </Box>
  )
}
